"use client";

import { useState, useMemo, useRef, useEffect } from "react";
import { MapPin, PhoneCall, Star, House, Zap, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * Emergency page — Section 5: Service Areas
 *
 * Mirrors homepage ServiceAreas 1:1 — dark navy gradient, gold hairline,
 * dot pattern, search + chip filter + region grid + CTA banner.
 *
 * Added two new regions:
 *   - Wollongong (Illawarra)
 *   - Central Coast
 *
 * Stat swaps for emergency context:
 *   - "7 days A Week" → "Same-Day Response"
 *   - Title accent: Sydney → Sydney, Wollongong & Central Coast
 */

const HEAD = {
  fontFamily: "'Manrope', sans-serif"
};
const LABEL = {
  fontFamily: "'Manrope', sans-serif"
};
const regions = [
// ----- SYDNEY (8 sub-regions) -----
{
  name: "Eastern Suburbs",
  suburbs: ["Bellevue Hill", "Bondi", "Bondi Beach", "Bondi Junction", "Botany", "Bronte", "Centennial Park", "Clovelly", "Coogee", "Double Bay", "Dover Heights", "Eastgardens", "Kensington", "Kingsford", "Maroubra", "Mascot", "Paddington", "Point Piper", "Randwick", "Rose Bay", "Vaucluse", "Watsons Bay", "Waverley", "Woollahra", "Zetland"]
}, {
  name: "Inner West",
  suburbs: ["Annandale", "Ashfield", "Balmain", "Burwood", "Camperdown", "Concord", "Croydon", "Dulwich Hill", "Enmore", "Five Dock", "Glebe", "Haberfield", "Leichhardt", "Marrickville", "Newtown", "Petersham", "Rozelle", "Stanmore", "Strathfield", "Summer Hill", "Surry Hills", "Tempe"]
}, {
  name: "North Shore",
  suburbs: ["Artarmon", "Castle Cove", "Chatswood", "Cremorne", "Crows Nest", "Frenchs Forest", "Gordon", "Hornsby", "Killara", "Kirribilli", "Lane Cove", "Lindfield", "Longueville", "Mosman", "Neutral Bay", "North Sydney", "Pymble", "Roseville", "Ryde", "St Ives", "Turramurra", "Wahroonga", "Willoughby"]
}, {
  name: "Northern Beaches",
  suburbs: ["Avalon", "Balgowlah", "Brookvale", "Collaroy", "Curl Curl", "Dee Why", "Freshwater", "Manly", "Mona Vale", "Narrabeen", "Newport", "Palm Beach", "Warriewood"]
}, {
  name: "Western Sydney",
  suburbs: ["Auburn", "Bankstown", "Bass Hill", "Blacktown", "Cabramatta", "Canterbury", "Chester Hill", "Fairfield", "Granville", "Greystanes", "Guildford", "Liverpool", "Merrylands", "Mount Druitt", "Parramatta", "Penrith", "Prospect", "Quakers Hill", "Rooty Hill", "Seven Hills", "Smithfield", "Toongabbie", "Wetherill Park", "Wentworthville", "Westmead", "Yagoona"]
}, {
  name: "Hills District",
  suburbs: ["Baulkham Hills", "Beaumont Hills", "Bella Vista", "Carlingford", "Castle Hill", "Cherrybrook", "Dural", "Eastwood", "Epping", "Glenhaven", "Kellyville", "Kellyville Ridge", "Marsden Park", "North Rocks", "Norwest", "Rouse Hill", "Schofields", "The Ponds", "West Pennant Hills"]
}, {
  name: "Sutherland Shire",
  suburbs: ["Caringbah", "Cronulla", "Engadine", "Gymea", "Heathcote", "Jannali", "Kirrawee", "Menai", "Miranda", "Sutherland", "Sylvania", "Taren Point"]
}, {
  name: "Sydney CBD & Surrounds",
  suburbs: ["Alexandria", "Chippendale", "Darlinghurst", "Darling Harbour", "Erskineville", "Haymarket", "Potts Point", "Pyrmont", "Redfern", "Surry Hills", "Sydney CBD", "Ultimo", "Waterloo", "Woolloomooloo"]
},
// ----- WOLLONGONG (Illawarra) -----
{
  name: "Wollongong",
  suburbs: ["Albion Park", "Austinmer", "Bellambi", "Berkeley", "Bulli", "Coledale", "Coniston", "Corrimal", "Dapto", "Fairy Meadow", "Figtree", "Gerringong", "Helensburgh", "Horsley", "Kiama", "Lake Heights", "Mount Kembla", "Mount Pleasant", "North Wollongong", "Oak Flats", "Port Kembla", "Shellharbour", "Shell Cove", "Thirroul", "Towradgi", "Unanderra", "Warrawong", "Warilla", "Windang", "Wollongong", "Woonona"]
},
// ----- CENTRAL COAST -----
{
  name: "Central Coast",
  suburbs: ["Avoca Beach", "Bateau Bay", "Berkeley Vale", "Budgewoi", "Copacabana", "East Gosford", "Empire Bay", "Ettalong Beach", "Erina", "Forresters Beach", "Gosford", "Kanwal", "Killarney Vale", "Kincumber", "Lake Haven", "Lake Munmorah", "Long Jetty", "MacMasters Beach", "Norah Head", "Pearl Beach", "Saratoga", "Shelly Beach", "Terrigal", "The Entrance", "Toukley", "Tuggerah", "Tumbi Umbi", "Umina Beach", "Wamberal", "West Gosford", "Woy Woy", "Wyong"]
}];
const totalSuburbs = regions.reduce((acc, r) => acc + r.suburbs.length, 0);
function highlight(text: string, q: string) {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q);
  if (i < 0) return text;
  return <>
      {text.slice(0, i)}
      <mark className="bg-[#F5A623]/25 text-[#081438] rounded px-0.5">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>;
}
export default function EmergencyServiceAreas() {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState("All");
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) setVisible(true);
    }, {
      threshold: 0.08
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    return regions.filter(r => active === "All" || r.name === active).map(r => ({
      name: r.name,
      suburbs: q ? r.suburbs.filter(s => s.toLowerCase().includes(q)) : r.suburbs
    })).filter(r => r.suburbs.length > 0);
  }, [active, q]);
  const matchCount = filtered.reduce((a, r) => a + r.suburbs.length, 0);
  const chips = ["All Areas", ...regions.map(r => r.name)];
  return <section id="emergency-service-areas" style={{
    ...HEAD,
    background: "linear-gradient(135deg, #081438 0%, #0d1e3a 100%)"
  }} className="relative w-full py-16 md:py-20 overflow-hidden text-white">
      <div className="absolute top-0 inset-x-0 h-[2px]" style={{
      background: "linear-gradient(to right, transparent, #F5A623, transparent)"
    }} />
      <div className="absolute inset-0 pointer-events-none z-0 opacity-[0.05]" style={{
      backgroundImage: "radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)",
      backgroundSize: "24px 24px"
    }} />

      <div ref={ref} className={`max-w-[1240px] mx-auto px-4 md:px-6 relative z-20 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>

        {/* ============ HEADER (title + body + stats) ============ */}
        <div className="grid md:grid-cols-[minmax(0,460px)_1fr] gap-10 md:gap-16 mb-10 items-start">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-0.5 bg-[#F5A623] rounded-lg shrink-0" />
              <span style={LABEL} className="text-[11px] font-bold tracking-[0.18em] uppercase !text-[rgba(245,166,35,1)]">Emergency Coverage</span>
            </div>
            <h2 style={HEAD} className="text-[clamp(28px,3.8vw,44px)] font-extrabold leading-[1.08] tracking-tight m-0">
              <span className="text-white">Same-day service across </span>
              <span style={{
              color: "#9A6400"
            }} className="!text-[rgba(245,166,35,1)]">Sydney, Wollongong &amp; Central Coast</span>
            </h2>
          </div>

          <div className="flex flex-col justify-center h-full">
            <p style={HEAD} className="text-white/60 text-[16px] font-medium leading-relaxed mb-7 max-w-2xl">
              Licensed, fully insured technicians providing fast emergency garage door repair — broken springs, dead motors, doors off track, snapped cables and more — across every suburb of Greater Sydney, the Illawarra and the Central Coast. Same-day service guaranteed.
            </p>
            <div className="flex flex-wrap gap-8 md:gap-12">
              <div>
                <div className="flex items-baseline gap-1">
                  <span style={HEAD} className="text-white text-[clamp(30px,3.6vw,44px)] font-extrabold leading-none">{totalSuburbs}</span>
                  <span className="text-lg font-extrabold !text-[rgba(245,166,35,1)]">+</span>
                </div>
                <div style={LABEL} className="text-white/50 text-[11px] font-bold tracking-[0.1em] uppercase mt-1.5 flex items-center gap-1.5">
                  <House aria-hidden="true" focusable="false" className="w-3 h-3" /> Suburbs Covered
                </div>
              </div>
              <div>
                <div className="flex items-baseline gap-1">
                  <span style={HEAD} className="text-white text-[clamp(30px,3.6vw,44px)] font-extrabold leading-none">5.0</span>
                  <Star aria-hidden="true" focusable="false" className="lucide lucide-star lucide lucide-star lucide lucide-star lucide lucide-star lucide lucide-star w-5 h-5 fill-[#9A6400] !text-[rgba(245,166,35,1)] !bg-[rgba(245,166,35,0)] md:w-7 md:h-7" />
                </div>
                <div style={LABEL} className="text-white/50 text-[11px] font-bold tracking-[0.1em] uppercase mt-1.5">Google Rating</div>
              </div>
              <div>
                <div className="flex items-baseline gap-1">
                  <span style={HEAD} className="text-white text-[clamp(30px,3.6vw,44px)] font-extrabold leading-none">Same</span>
                  <span className="text-white text-sm font-bold">Day</span>
                </div>
                <div style={LABEL} className="text-white/50 text-[11px] font-bold tracking-[0.1em] uppercase mt-1.5 flex items-center gap-1.5">
                  <Zap aria-hidden="true" focusable="false" className="w-3 h-3" /> Emergency Response
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ============ SEARCH + CHIP FILTERS ============ */}
        <div className="mb-8">
          <div className="relative max-w-[560px]">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-white/40 pointer-events-none">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} aria-label="Search your suburb" placeholder="Find your suburb — e.g. Bondi, Wollongong, Terrigal…" style={HEAD} className="w-full h-14 pl-12 pr-11 rounded-xl bg-white/5 border border-white/15 text-white text-[15px] font-medium placeholder:text-white/40 outline-none transition-all focus:border-[#F5A623] focus:bg-white/10 focus:ring-4 focus:ring-[#F5A623]/10" />
            {query && <button onClick={() => setQuery("")} aria-label="Clear search" className="absolute right-3 top-1/2 -translate-y-1/2 size-7 rounded-lg flex items-center justify-center text-white/40 hover:bg-white/10 hover:text-white transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" className="size-4">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>}
          </div>

          {q && (matchCount > 0 ? <div className="mt-4 inline-flex items-center gap-2.5 rounded-xl bg-[#16a34a]/8 border border-[#16a34a]/25 px-4 py-2.5">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false" className="size-5 text-[#16a34a] shrink-0">
                  <circle cx="12" cy="12" r="10" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                <span style={HEAD} className="text-[14px] font-semibold text-[#0f5132]">
                  Great news — we service <strong>{matchCount}</strong> area{matchCount > 1 ? "s" : ""} matching “{query}”.
                </span>
              </div> : <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl bg-[#F5A623]/8 border border-[#F5A623]/30 px-4 py-3">
                <span style={HEAD} className="text-[14px] font-semibold text-[#7a5200]">
                  We couldn't find “{query}” — but our mobile team likely still covers you.
                </span>
                <Link to="tel:0485000933" aria-label="Call Gold Garage Door to confirm service area" style={LABEL} className="inline-flex items-center gap-1.5 text-[#081438] bg-[#F5A623] hover:bg-[#d4a017] font-bold text-[13px] px-3.5 py-2 rounded-lg no-underline transition-colors shrink-0">
                  <PhoneCall aria-hidden="true" focusable="false" className="size-4" /> Call to confirm →
                </Link>
              </div>)}

          <div className="mt-5 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {chips.map((name, i) => {
            const isActive = name === "All Areas" && active === "All" || name === active;
            return <button key={name} data-index={i} onClick={() => {
              setActive(name === "All Areas" ? "All" : name);
            }} style={LABEL} className={`shrink-0 px-4 py-2 rounded-lg text-[13px] font-bold tracking-wide whitespace-nowrap border transition-all ${isActive ? "bg-[#F5A623] border-[#F5A623] text-[#081438] shadow-sm" : "bg-white/5 border-white/15 text-white/70 hover:border-[#F5A623] hover:text-white"}`}>
                  {name}
                </button>;
          })}
          </div>
        </div>

        {/* ============ REGIONS GRID ============ */}
        {matchCount > 0 && <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-8">
            {filtered.map((region, i) => <div key={region.name} data-index={i}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-2 h-2 bg-[#F5A623] rounded-sm shrink-0" />
                  <h3 style={LABEL} className="text-white text-[12px] md:text-[13px] font-bold tracking-[0.12em] uppercase whitespace-nowrap m-0">
                    {region.name}
                  </h3>
                  <div className="flex-1 h-px bg-white/15" />
                  <span style={LABEL} className="text-[11px] text-white/45 font-medium shrink-0">{region.suburbs.length} suburbs</span>
                </div>

                <div className="flex flex-wrap gap-2">
                  {region.suburbs.map(suburb => <span key={suburb} className="flex items-center gap-2 bg-white/5 border border-white/12 rounded-lg px-3 py-2 hover:border-[#F5A623] hover:bg-[#F5A623]/[0.05] hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200 group cursor-default">
                      <MapPin aria-hidden="true" focusable="false" className="w-3 h-3 text-white/40 group-hover:text-[#F5A623] transition-colors shrink-0" />
                      <span style={HEAD} className="text-white/90 text-[13px] font-semibold whitespace-nowrap">
                        {highlight(suburb, q)}
                      </span>
                    </span>)}
                </div>
              </div>)}
          </div>}

        {/* ============ BOTTOM CTA BANNER ============ */}
        <div className="mt-12 relative overflow-hidden rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-8 border border-[#F5A623]/30" style={{
        background: "linear-gradient(135deg, #081438 0%, #0d1e3a 100%)"
      }}>
          <div className="absolute -top-16 -right-16 w-52 h-52 rounded-lg bg-[#F5A623]/[0.05] pointer-events-none" />
          <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-lg bg-[#F5A623]/[0.04] pointer-events-none" />

          <div className="relative z-10 text-center md:text-left">
            <h4 style={HEAD} className="text-[22px] md:text-[26px] font-extrabold tracking-tight text-white m-0 mb-2 leading-tight">
              Don't see your suburb? <span className="!text-[rgba(245,166,35,1)]">We still come to you.</span>
            </h4>
            <p style={HEAD} className="text-white/60 font-medium text-[15px] m-0">
              Our mobile team covers Greater Sydney, the Illawarra and the Central Coast — same-day emergency service guaranteed.
            </p>
          </div>

          <div className="relative z-10 flex flex-col sm:flex-row gap-3 shrink-0">
            <Link to="tel:0485000933" aria-label="Call Gold Garage Door" style={LABEL} className="inline-flex items-center justify-center gap-2.5 bg-[#F5A623] text-[#081438] font-bold text-[15px] tracking-wide h-14 px-8 rounded-xl hover:bg-[#d4a017] hover:-translate-y-0.5 hover:shadow-xl transition-all no-underline">
              <PhoneCall aria-hidden="true" focusable="false" className="w-5 h-5" /> Call — 0485 000 933
            </Link>
            <Link to="https://wa.me/61485000933?text=Hi%20Gold%20Garage%20Door%2C%20do%20you%20service%20my%20area%3F" aria-label="Message Gold Garage Door on WhatsApp" style={LABEL} className="inline-flex items-center justify-center gap-2 bg-transparent border-[1.5px] border-white/15 text-white font-bold text-[14px] h-14 px-6 rounded-xl hover:border-[#F5A623] hover:bg-[#F5A623]/[0.05] transition-all no-underline">
              WhatsApp Us <ChevronRight aria-hidden="true" focusable="false" className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>;
}